import { Card, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { type FC, useEffect, useRef, useState } from 'react';
import Carousel from 'react-multi-carousel';
import { AvaniArticleCard } from './AvaniArticleCard';
import 'react-multi-carousel/lib/styles.css';
import type { AvaniBlogArticle } from '../../models';

interface AvaniArticlesCardProps {
  articles: AvaniBlogArticle[] | null;
}

const AvaniStyledCard = styled(Card)(
  ({ theme }) => `
  border-radius: 16px;
  padding: 16px;
  background-color: ${theme.colors.avani.lightTurc};
  height: 100%;

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0;
    line-height: 1;
    padding-bottom: 12px;
  }
`
);

const CARD_WIDTH = 360;
const CARD_GAP = 24;

const decodeHtmlEntities = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.documentElement.textContent || '';
};

export const AvaniArticlesCard: FC<AvaniArticlesCardProps> = ({ articles }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  const [itemsToShow, setItemsToShow] = useState(1);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      const containerWidth = entry.contentRect.width;
      const fullCardWidth = CARD_WIDTH + CARD_GAP;
      const count = (containerWidth - CARD_GAP) / fullCardWidth;
      setItemsToShow(count);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const responsiveCarouselConfig = {
    desktop: {
      breakpoint: { max: 4000, min: 0 },
      items: itemsToShow,
      partialVisibilityGutter: CARD_GAP,
      slidesToSlide: articles?.length ?? 0
    }
  };

  if (articles)
    return (
      <AvaniStyledCard ref={containerRef} raised={false}>
        <Typography variant={'h3'} sx={{ color: theme.colors.avani.teal }}>
          Avani Stories
        </Typography>
        <Typography fontSize={14} variant={'body2'} fontWeight={'normal'} sx={{ marginBottom: '24px' }}>
          Catch up on the latest news, articles and case studies, providing helpful tips and inspiration to keep you
          pouring perfect pints.
        </Typography>
        <Carousel
          swipeable
          draggable
          arrows
          partialVisbile
          responsive={responsiveCarouselConfig}
          autoPlaySpeed={3000}
          keyBoardControl={true}
          transitionDuration={1000}
          shouldResetAutoplay
          containerClass="carousel-container"
          dotListClass="custom-dot-list-style"
          itemClass="carousel-item-padding-40-px"
        >
          {articles.map((article, ind) => {
            return (
              <AvaniArticleCard
                key={ind}
                title={decodeHtmlEntities(article.title.rendered)}
                link={article.link}
                image={article.featured_img_url}
              />
            );
          })}
        </Carousel>
      </AvaniStyledCard>
    );
  else return <></>;
};
